/**
 * SldDocumentRegister.jsx — SLD Document Register
 *
 * Mirrors the PFD/PID Document Register UX (Documents tab with
 * View / Edit per row, plus an SLD Viewer tab that renders inside the
 * Autodesk Forge (APS) Viewer via <ApsDrawingViewer/>, plus a fall-back
 * Editor tab that wraps the unchanged <SLDEditor/>).
 *
 *   Documents tab  → list of SLD sheets (from /sld/register) with
 *                    View / Edit buttons.
 *   SLD Viewer tab → ApsDrawingViewer for the active sheet.
 *   SLD Editor tab → unchanged SLDEditor component (per user spec).
 *
 * Workflow:
 *   - "View" calls POST /sld/sheets/{sheet_no}/render-svg first so the
 *     latest editor state is rendered into drawing_sheets, then opens
 *     the APS Viewer pointed at that sheet. The viewer's "Generate
 *     APS view" / "Refresh APS view" buttons handle the translation
 *     from drawing_sheets.canvas_svg → SVF2.
 *   - "Edit" jumps to the Editor tab (which is the existing SLDEditor
 *     verbatim).
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Eye, Pencil, RefreshCcw, Loader2,
   AlertCircle, FileText, Cable, Zap }
  from "lucide-react";
import ApsDrawingViewer from "./ApsDrawingViewer";
import SLDEditor from "../engineering/editors/SLDEditor";

const SHEET_TYPE_LABEL = "Single Line Diagram";

export default function SldDocumentRegister({ projectId }) {
   const [tab, setTab] = useState("documents");      // documents | viewer | editor
   const [register, setRegister] = useState(null);
   const [activeSheet, setActiveSheet] = useState(null);
   const [busy, setBusy] = useState({});             // sheet_no -> bool
   const [err, setErr] = useState("");

   const loadRegister = useCallback(async () => {
      if (!projectId) return;
      setErr("");
      try {
         const r = await api.get(`/projects/${projectId}/sld/register`);
         setRegister(r.data);
         if (!activeSheet && r.data?.sheets?.length) {
            setActiveSheet(r.data.sheets[0].sheet_no);
         }
      } catch (e) {
         setErr(formatErr(e?.response?.data?.detail) || e.message);
      }
   }, [projectId, activeSheet]);

   useEffect(() => { loadRegister(); }, [loadRegister]);

   // "View" — re-render the SVG (so the viewer sees the latest editor
   // state) and switch to the viewer tab.
   const openInViewer = useCallback(async (sheet_no) => {
      setBusy((b) => ({ ...b, [sheet_no]: true }));
      try {
         await api.post(
            `/projects/${projectId}/sld/sheets/${encodeURIComponent(sheet_no)}/render-svg`);
         setActiveSheet(sheet_no);
         setTab("viewer");
         // refresh register so has_svg = true reflects.
         loadRegister();
      } catch (e) {
         setErr(formatErr(e?.response?.data?.detail) || e.message);
      } finally {
         setBusy((b) => { const n = { ...b }; delete n[sheet_no]; return n; });
      }
   }, [projectId, loadRegister]);

   const openInEditor = useCallback((sheet_no) => {
      setActiveSheet(sheet_no);
      setTab("editor");
   }, []);

   const sheets = register?.sheets || [];

   return (
      <div data-testid="sld-document-register"
            style={{ display: "flex", flexDirection: "column", flex: 1,
                        minHeight: 0, height: "100%",
                        fontFamily: "'JetBrains Mono', monospace",
                        color: "var(--pf-text)" }}>
         {/* Header */}
         <div style={{ display: "flex", padding: "10px 16px",
                          borderBottom: "1px solid var(--pf-border)",
                          background: "var(--pf-surface)",
                          alignItems: "center", gap: 14 }}>
            <Zap size={15} style={{ color: "#F26B1F" }}/>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#F26B1F",
                              letterSpacing: 0.6, textTransform: "uppercase" }}>
               Electrical SLD — Document Register
            </div>
            <span style={{ flex: 1 }}/>
            <SubTab label="Documents" active={tab === "documents"}
               onClick={() => setTab("documents")}
               testId="sld-tab-documents"
               badge={`${sheets.length}`}/>
            <SubTab label="SLD Viewer" active={tab === "viewer"}
               onClick={() => setTab("viewer")}
               testId="sld-tab-viewer"/>
            <SubTab label="Editor" active={tab === "editor"}
               onClick={() => setTab("editor")}
               testId="sld-tab-editor"/>
         </div>

         {err && (
            <div style={{ padding: "8px 16px", color: "#fca5a5",
                              fontSize: 11,
                              background: "rgba(127,29,29,0.18)",
                              borderBottom: "1px solid rgba(220,38,38,0.4)" }}>
               <AlertCircle size={11} style={{ verticalAlign: -1,
                  marginRight: 5 }}/>{err}
            </div>
         )}

         <div style={{ flex: 1, minHeight: 0, display: "flex",
                          flexDirection: "column" }}>
            {tab === "documents" && (
               <DocumentsTable
                  projectId={projectId}
                  register={register}
                  busy={busy}
                  onView={openInViewer}
                  onEdit={openInEditor}/>
            )}

            {tab === "viewer" && (
               activeSheet ? (
                  <ApsDrawingViewer
                     key={activeSheet}
                     projectId={projectId}
                     sheetId={activeSheet}
                     docKind="sld"
                     title={(sheets.find((s) => s.sheet_no === activeSheet)
                             ?.title) || activeSheet}
                     onExit={() => setTab("documents")}/>
               ) : (
                  <CenteredHint icon={<FileText size={26}/>}
                     msg={(
                       <>
                         Click <b style={{ color: "#F26B1F" }}>View</b>
                         &nbsp;on an SLD sheet in the
                         <b style={{ color: "#F26B1F" }}>&nbsp;Documents</b>
                         &nbsp;tab to open it in the Autodesk Forge viewer.
                       </>
                     )}/>
               )
            )}

            {tab === "editor" && (
               <div style={{ flex: 1, minHeight: 0, overflow: "auto",
                                padding: 8 }}>
                  <SLDEditor projectId={projectId}/>
               </div>
            )}
         </div>
      </div>
   );
}

// ── Documents table (View / Edit per row) ─────────────────
function DocumentsTable({ projectId, register, busy, onView, onEdit }) {
   const sheets = register?.sheets || [];
   if (!sheets.length) {
      return (
         <CenteredHint icon={<Cable size={26}/>}
            msg={(
               <>
                  No SLD sheets yet. Switch to the
                  <b style={{ color: "#F26B1F" }}>&nbsp;Editor&nbsp;</b>
                  tab to start placing transformers / MCCs / motors. New
                  sheets you add there appear here automatically.
               </>
            )}/>
      );
   }
   return (
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 16 }}>
         <div style={{ fontSize: 12, color: "var(--pf-muted)",
                           marginBottom: 10 }}>
            {register?.project_name || ""} — {sheets.length} SLD sheet{sheets.length === 1 ? "" : "s"} ·
            &nbsp;{register?.total_equipment || 0} equipment ·
            &nbsp;{register?.total_feeders || 0} feeders
         </div>
         <table data-testid="sld-register-table"
                  style={{ width: "100%", borderCollapse: "collapse",
                              fontSize: 12 }}>
            <thead>
               <tr style={{ textAlign: "left", color: "var(--pf-muted)",
                                borderBottom: "1px solid var(--pf-border)" }}>
                  <Th>#</Th>
                  <Th>Document No.</Th>
                  <Th>Title</Th>
                  <Th align="right">Equipment</Th>
                  <Th align="right">Feeders</Th>
                  <Th>Status</Th>
                  <Th>Action</Th>
               </tr>
            </thead>
            <tbody>
               {sheets.map((s, idx) => (
                  <tr key={s.sheet_no}
                       data-testid={`sld-register-row-${s.sheet_no}`}
                       style={{ borderBottom: "1px solid var(--pf-border-subtle, rgba(148,163,184,0.10))" }}>
                     <Td muted>{idx + 1}</Td>
                     <Td>
                        <span style={{ color: "#F26B1F", fontWeight: 600 }}>
                           {s.sheet_no}
                        </span>
                     </Td>
                     <Td>
                        <div style={{ fontWeight: 500 }}>{s.title}</div>
                        <div style={{ color: "var(--pf-muted)", fontSize: 10 }}>
                           {SHEET_TYPE_LABEL}
                        </div>
                     </Td>
                     <Td align="right">{s.equipment_count}</Td>
                     <Td align="right">{s.feeder_count}</Td>
                     <Td>
                        {s.has_content ? (
                           <Pill color="#22c55e" bg="rgba(34,197,94,0.15)"
                                  border="#22c55e">complete</Pill>
                        ) : (
                           <Pill color="#facc15"
                                  bg="rgba(234,179,8,0.15)"
                                  border="#facc15">empty</Pill>
                        )}
                     </Td>
                     <Td>
                        <div style={{ display: "flex", gap: 6 }}>
                           <ActionBtn testId={`sld-view-${s.sheet_no}`}
                              onClick={() => onView(s.sheet_no)}
                              busy={busy[s.sheet_no]}
                              tone="primary">
                              {busy[s.sheet_no]
                                ? <Loader2 size={11} className="animate-spin"/>
                                : <Eye size={11}/>} View
                           </ActionBtn>
                           <ActionBtn testId={`sld-edit-${s.sheet_no}`}
                              onClick={() => onEdit(s.sheet_no)}
                              tone="ghost">
                              <Pencil size={11}/> Edit
                           </ActionBtn>
                        </div>
                     </Td>
                  </tr>
               ))}
            </tbody>
         </table>
      </div>
   );
}

// ── small helpers ─────────────────────────────────────────
function SubTab({ label, active, onClick, testId, badge }) {
   return (
      <button data-testid={testId} onClick={onClick}
         style={{
            background: active ? "rgba(242, 107, 31, 0.15)" : "transparent",
            color: active ? "#F26B1F" : "var(--pf-text-muted, #94a3b8)",
            border: `1px solid ${active ? "#F26B1F" : "var(--pf-border)"}`,
            padding: "5px 12px", borderRadius: 4, cursor: "pointer",
            fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            display: "inline-flex", alignItems: "center", gap: 6,
            fontWeight: active ? 700 : 500,
         }}>
         {label}
         {badge && (
            <span style={{
               background: active ? "#F26B1F" : "var(--pf-surface-2, #1e293b)",
               color: active ? "#fff" : "var(--pf-text-muted)",
               padding: "1px 6px", borderRadius: 999,
               fontSize: 10, fontWeight: 700 }}>{badge}</span>
         )}
      </button>
   );
}

function Th({ children, align = "left" }) {
   return <th style={{ padding: "8px 10px", textAlign: align,
                              fontWeight: 600, fontSize: 10,
                              letterSpacing: 0.4, textTransform: "uppercase",
                              color: "var(--pf-muted)" }}>{children}</th>;
}

function Td({ children, align = "left", muted }) {
   return <td style={{ padding: "10px 10px", textAlign: align,
                              color: muted ? "var(--pf-muted)" : "var(--pf-text)",
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 12, verticalAlign: "top" }}>{children}</td>;
}

function Pill({ children, color, bg, border }) {
   return (
      <span style={{
         color, background: bg,
         border: `1px solid ${border}`,
         padding: "2px 8px", borderRadius: 999,
         fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
         letterSpacing: 0.4 }}>
         {children}
      </span>
   );
}

function ActionBtn({ children, onClick, busy, tone = "ghost", testId }) {
   const styles = tone === "primary" ? {
      background: "rgba(242, 107, 31, 0.18)", color: "#F26B1F",
      border: "1px solid #F26B1F",
   } : {
      background: "transparent",
      color: "var(--pf-text)",
      border: "1px solid var(--pf-border)",
   };
   return (
      <button data-testid={testId} onClick={onClick} disabled={busy}
         style={{ ...styles,
            padding: "4px 10px", borderRadius: 4,
            cursor: busy ? "wait" : "pointer", fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            display: "inline-flex", alignItems: "center", gap: 4 }}>
         {children}
      </button>
   );
}

function CenteredHint({ icon, msg }) {
   return (
      <div style={{ flex: 1, display: "flex", alignItems: "center",
            justifyContent: "center", color: "var(--pf-muted)",
            textAlign: "center", padding: 32 }}>
         <div style={{ maxWidth: 520, display: "flex", flexDirection: "column",
               alignItems: "center", gap: 14 }}>
            <div style={{ color: "#F26B1F" }}>{icon}</div>
            <div style={{ fontSize: 12, lineHeight: 1.6 }}>{msg}</div>
         </div>
      </div>
   );
}
